import { ReactNode } from 'react'
import { useAuth } from './KeycloakProvider'

interface ProtectedRouteProps {
    children: ReactNode
    requiredRoles?: string[]
}

/**
 * Route guard that requires authentication.
 *
 * Because the app uses keycloak-js with `onLoad: 'login-required'`, users are
 * normally redirected to Keycloak before the SPA renders.  If for any reason
 * the Keycloak init fails and the user reaches this component without being
 * authenticated, we re-trigger the Keycloak login rather than navigating to
 * a (non-existent) `/login` route.
 */
export function ProtectedRoute({ children, requiredRoles = [] }: ProtectedRouteProps) {
    const { isAuthenticated, isLoading, hasRole, login } = useAuth()

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner" />
                <p>Checking authentication...</p>
            </div>
        )
    }

    if (!isAuthenticated) {
        // Re-trigger Keycloak login — keycloak-js handles the redirect.
        login()
        return (
            <div className="loading-container">
                <div className="loading-spinner" />
                <p>Redirecting to login...</p>
            </div>
        )
    }

    // Check required roles if specified
    if (requiredRoles.length > 0) {
        const hasRequiredRole = requiredRoles.some((role) => hasRole(role))
        if (!hasRequiredRole) {
            return (
                <div className="access-denied">
                    <h2>Access Denied</h2>
                    <p>You do not have permission to access this page.</p>
                </div>
            )
        }
    }

    return <>{children}</>
}
